
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { formatDate } from '../hooks/useCustomersData/utils';

interface Order {
  id: string;
  order_number: string;
  total_amount: number;
  created_at: string;
  status: string;
  payment_status: string;
}

interface CustomerOrdersTabProps {
  orders?: Order[];
  totalSpent?: number;
  organizationId?: string | null;
}

const CustomerOrdersTab = ({ orders = [], totalSpent = 0, organizationId }: CustomerOrdersTabProps) => {
  const hasOrders = orders && orders.length > 0;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Historique des commandes</CardTitle>
        {hasOrders && (
          <div className="text-sm text-muted-foreground">
            <span className="font-semibold">Total dépensé: </span>
            <span className="text-primary">{totalSpent.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {hasOrders ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Commande</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Paiement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-medium">{order.order_number}</TableCell>
                  <TableCell>{formatDate(order.created_at)}</TableCell>
                  <TableCell>{Number(order.total_amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs ${getPaymentStatusClass(order.payment_status)}`}>
                      {getPaymentStatusLabel(order.payment_status)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Aucune commande trouvée pour ce client
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Helper functions for status styling
const getStatusClass = (status: string) => {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'processing': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'completed': return 'Terminée';
    case 'processing': return 'En cours';
    case 'pending': return 'En attente';
    case 'cancelled': return 'Annulée';
    default: return status;
  }
};

const getPaymentStatusClass = (status: string) => {
  switch (status) {
    case 'paid': return 'bg-green-100 text-green-800';
    case 'unpaid': return 'bg-red-100 text-red-800';
    case 'partial': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getPaymentStatusLabel = (status: string) => {
  switch (status) {
    case 'paid': return 'Payée';
    case 'unpaid': return 'Non payée';
    case 'partial': return 'Partiel';
    default: return status;
  }
};

export default CustomerOrdersTab;
